/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

/* eslint-disable no-nested-ternary */
import { Tooltip, Typography, message } from 'antd';
import { Button } from '@/uiKit';
import React, { useMemo } from 'react';
import PlaygroundPlayIcon from '@/components/icons/PlaygroundPlayIcon';
import PlaygroundPauseIcon from '@/components/icons/PlaygroundPauseIcon';
import { useRunCtxV2 } from '@/provider/runv2';
import {
  EditOutlined,
  MessageOutlined,
  WarningOutlined,
  RedoOutlined,
  CopyOutlined
} from '@ant-design/icons';
import NewProcedureFromRun from '@/components/NewProcedureFromRun/NewProcedureFromRun';
import classnames from 'classnames';
import AccessControl from '@/components/AccessControl/AccessControl';
import { ProcessPermissionsEnum } from '@/utils/permissionConstants';
import { trackEvent } from '@/tracker';
import { AnalyticsEvents } from '@/analytics/events';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { useOutletContext } from 'react-router-dom';
import { IProcedureOutletContext } from '@/pages/RunViewLayout';
import useALC from '@/hooks/useALCFlag';
import ReplayRun from '@/components/ReplayRun/ReplayRun';
import { last } from 'lodash';
import { useKogEditorContext } from '../Editor/EditorProvider';
import styles from './EditorHeader.module.less';
import { EditorSourceType } from '../Editor/editorInterface';
import { textDeFormatter } from '../EditorWidgets/decorators';
import RetryRun from '../../../../components/RetryRun/RetryRun';
import WorkerStatusInfo from '../WorkerStatusInfo/WorkerStatusInfo';
import useDepartment from '../../../../hooks/useDepartment';

interface EditorHeaderProps {
  workerId: string;
  isKonciergeVisible?: boolean;
  miniPlayground?: {
    documentLineId: string;
    onReset?: () => void;
  };
  documentToken?: string;
}

function EditorHeader(props: EditorHeaderProps) {
  const { workerId, isKonciergeVisible, miniPlayground, documentToken } = props;

  const { worker } = useRunCtxV2();
  const outletContext = useOutletContext<IProcedureOutletContext>();
  const { source, editorContent, editorStatus, actions, replacerObj } =
    useKogEditorContext();
  const [{ rolloutRestartPauseWorker }] = useFeatureFlag();
  const { isALCEnabled } = useALC();
  const { department } = useDepartment();

  const [{ rolloutTechniqueV2 }] = useFeatureFlag({
    context: { department: { key: department.id, dept_id: department.id } }
  });

  const isMiniPlayground = source === EditorSourceType.MINI_PLAYGROUND;
  const isRunPage = source === EditorSourceType.PROCESS_RUN;

  const showKonciergeChat = useMemo(
    () =>
      // KOG-8087 Hide Koncierge chat (kchat) as it's beta and we are using intercom AI now
      // Keeping the logic in case we want to revert back
      false && !editorStatus.readOnly && !isMiniPlayground,
    [editorStatus.readOnly, isMiniPlayground]
  );

  const workerState = useMemo(() => {
    let state = worker?.state || null;

    // use forkExecutionInfo for status if rolloutTechniqueV2 is enabled
    if (
      rolloutTechniqueV2 &&
      worker?.forkExecutionInfo?.items &&
      isMiniPlayground
    ) {
      const currentExecutionList = worker.forkExecutionInfo.items.filter(
        (info) =>
          info.documentToken === documentToken &&
          info.lineId === miniPlayground?.documentLineId
      );
      const currentExecution = last(currentExecutionList);
      if (currentExecution) {
        state = currentExecution.internalState;
      }
    }

    return state;
  }, [
    worker?.state,
    worker?.forkExecutionInfo,
    documentToken,
    miniPlayground?.documentLineId,
    rolloutTechniqueV2,
    isMiniPlayground
  ]);

  const pauseHandler = () => {
    // TODO: Implement Pause Worker when Pause feature is enabled in the backend
    // if (editorStatus.readOnly) {
    //   return;
    // }
    // pauseWorker({
    //   variables: {
    //     workerId
    //   },
    //   onCompleted: () => {
    //     actions.updateEditorStatus({ isExecuting: false, isEditing: true });
    //   }
    // });
  };

  const handleEditorContentCopy = () => {
    if (editorContent) {
      navigator.clipboard.writeText(editorContent).then(() => {
        message.success(`Copied to clipboard`);
      });
    }
  };

  return (
    <div className={styles.editorHeader}>
      <div className={styles.editorProgress}>
        {worker?.id ? (
          <WorkerStatusInfo
            worker={worker}
            state={workerState}
            currentDocumentToken={documentToken}
          />
        ) : (
          <div />
        )}
      </div>
      <div className={styles.editorButtons}>
        {/* <Switch
          checkedChildren="Edit Mode"
          unCheckedChildren="Play Mode"
          onChange={onModeChange}
          checked={editMode}
        /> */}
        {!isMiniPlayground && (
          <Typography.Text type="secondary" data-cy="document-status">
            {editorStatus.isExecuting
              ? 'Sent for execution'
              : editorStatus.isSaving
              ? 'Saving...'
              : 'Saved'}
          </Typography.Text>
        )}

        {!editorStatus.isExecuting ? (
          <Tooltip
            title={
              editorStatus.executionEnabled ? (
                'Run'
              ) : (
                <div data-cy="document-run-button-tooltip">
                  <WarningOutlined /> Parse Error;
                  <br />
                  <span style={{ fontSize: 10 }}>
                    Scrolling may be required to see all issues.
                  </span>
                </div>
              )
            }
          >
            <Button
              type="text"
              shape="default"
              className="display-flex align-center justify-center"
              onClick={() =>
                actions.onPlay(workerId, {
                  shouldStartExecution: true,
                  defaultRun: true
                })
              }
              icon={<PlaygroundPlayIcon />}
              disabled={!editorStatus.executionEnabled}
              data-cy={
                isMiniPlayground
                  ? 'mp-document-run-button'
                  : 'document-run-button'
              }
              size={isMiniPlayground ? 'small' : undefined}
            />
          </Tooltip>
        ) : (
          <Tooltip title="Pause">
            <Button
              type="text"
              shape="default"
              className={classnames(
                'display-flex align-center justify-center',
                {
                  [styles.activeButton]: !editorStatus.readOnly
                }
              )}
              onClick={pauseHandler}
              icon={<PlaygroundPauseIcon />}
              size={isMiniPlayground ? 'small' : undefined}
              data-cy="document-pause-button"
            />
          </Tooltip>
        )}

        {!isMiniPlayground && !isRunPage && rolloutRestartPauseWorker && (
          <Tooltip title="Restart">
            <Button
              type="text"
              shape="default"
              className={classnames(
                'display-flex',
                'align-center',
                'justify-center',
                styles.restartButton
              )}
              data-cy="document-restart-button"
              onClick={() => {
                trackEvent(AnalyticsEvents.PLAYGROUND.PGRestartButton, {
                  playgroundId: workerId
                });
                actions.onRestart(workerId);
              }}
              icon={<RedoOutlined />}
            />
          </Tooltip>
        )}

        {!isMiniPlayground && isRunPage ? (
          <Tooltip title="Edit Run">
            <Button
              type="text"
              shape="default"
              data-cy="document-edit-button"
              className={classnames(
                'display-flex align-center justify-center',
                {
                  [styles.activeButton]: !editorStatus.readOnly
                }
              )}
              onClick={() => {
                actions.updateEditorStatus({
                  readOnly: !editorStatus.readOnly
                });
              }}
              icon={
                <EditOutlined style={{ color: '#748F9E', fontSize: '18px' }} />
              }
            />
          </Tooltip>
        ) : null}

        {!isALCEnabled && !isMiniPlayground ? (
          <AccessControl
            requiredPermissions={[
              ProcessPermissionsEnum.ProcessRead,
              ProcessPermissionsEnum.ProcessCreate,
              ProcessPermissionsEnum.ProcessUpdate
            ]}
            RestrictedPage={null}
          >
            <NewProcedureFromRun
              editorContent={textDeFormatter(editorContent, replacerObj)}
              workerId={worker?.id}
              procedure={outletContext?.procedure}
              isRunPage={isRunPage}
            />
          </AccessControl>
        ) : null}

        {source !== EditorSourceType.PLAYGROUND &&
        source !== EditorSourceType.MINI_PLAYGROUND &&
        worker?.id ? (
          <RetryRun
            workerId={worker.id}
            isPlayground={!outletContext?.procedure}
          />
        ) : null}

        <ReplayRun
          worker={worker}
          isRunPage={isRunPage}
          isMiniPlayground={isMiniPlayground}
        />

        {/*
          KOG-8087 Hide Koncierge chat (kchat) as it's beta and we are using intercom AI now
          Keeping the logic in case we want to revert back 
        */}
        {showKonciergeChat && (
          <Tooltip title="Koncierge">
            <Button
              type="text"
              shape="default"
              data-cy="koncierge-button"
              className={classnames(
                'display-flex align-center justify-center',
                {
                  [styles.activeButton]: isKonciergeVisible
                }
              )}
              onClick={() => {
                trackEvent(
                  editorStatus.showChat
                    ? AnalyticsEvents.PLAYGROUND.PGKonciergeButtonClose
                    : AnalyticsEvents.PLAYGROUND.PGKonciergeButtonOpen,
                  {
                    playgroundId: workerId
                  }
                );
                const showChat = !editorStatus.showChat;
                actions.updateEditorStatus({
                  showChat,
                  showOutputPanel: !showChat
                });
              }}
              icon={
                <MessageOutlined style={{ height: '20px', width: '20px' }} />
              }
            />
          </Tooltip>
        )}

        {isMiniPlayground && (
          <Tooltip title="Copy commands">
            <Button
              type="text"
              shape="default"
              className={classnames(
                'display-flex align-center justify-center',
                styles.copyCommandsButton
              )}
              icon={<CopyOutlined />}
              size="small"
              data-cy="document-commands-copy-button"
              disabled={!editorContent}
              onClick={handleEditorContentCopy}
            />
          </Tooltip>
        )}

        {isMiniPlayground && (
          <Tooltip title="Restart">
            <Button
              type="text"
              shape="default"
              className={classnames(
                'display-flex',
                'align-center',
                'justify-center',
                styles.resetButton
              )}
              data-cy="document-reset-button"
              icon={<RedoOutlined />}
              onClick={miniPlayground?.onReset}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default EditorHeader;
